import { affiliateApi, brandApi, api } from "../lib/axios";

export interface RewardTier {
  tier: string;
  threshold: number;
  rewards: string[];
  icon: string;
}

export interface CurrentReward {
  id: string;
  rewardName: string;
  rewardTypes: string[];
  earnedAmount: number;
  rewardStatus: string;
  earnedAt: string;
  icon: string;
  tier: string;
}

export interface LeaderboardEntry {
  name: string;
  earnings: number;
  tier: string;
  rewardName: string;
}

export interface CurrentUserRewardResponse {
  currentReward: CurrentReward | null;
  totalEarnings: number;
  currentTierIndex: number;
  nextTierIndex: number | null;
  progress: number;
  remainingToNext: number;
  rewardTiers: RewardTier[];
  usersWonCurrentReward: number;
  topLeaderboard: LeaderboardEntry[];
  userType: "affiliate" | "brand";
  hasAchievedTier: boolean;
}

export interface GenerateShareTokenResponse {
  shareToken: string;
  shareUrl: string;
}

export interface PublicRewardProfile {
  name: string;
  userType: "affiliate" | "brand";
  currentReward: {
    rewardName: string;
    rewardTypes: string[];
    tier: string;
    icon: string;
  } | null;
  totalEarnings: number;
  earnedAt: string | null;
  rewardTiers: RewardTier[];
  currentTierIndex: number;
  progress: number;
  remainingToNext: number;
  hasAchievedTier: boolean;
}

// Helper to get the correct API instance based on user role
const getApiInstance = () => {
  // Check if user is brand
  const brandToken = localStorage.getItem("brand_token");
  if (brandToken) {
    return brandApi;
  }

  // Default to affiliate
  return affiliateApi;
};

export const rewardsService = {
  /**
   * Get current user's reward status
   * Works for both authenticated affiliates and brands
   */
  async getCurrentUserRewards(): Promise<CurrentUserRewardResponse> {
    const apiInstance = getApiInstance();
    const response = await apiInstance.get<CurrentUserRewardResponse>(
      "/rewards/current"
    );
    return response.data;
  },

  /**
   * Generate a shareable token for current user's reward profile
   * Works for both authenticated affiliates and brands
   */
  async generateShareToken(): Promise<GenerateShareTokenResponse> {
    const apiInstance = getApiInstance();
    const response = await apiInstance.post<GenerateShareTokenResponse>(
      "/rewards/generate-share-token"
    );
    return response.data;
  },

  /**
   * Get public reward profile by token
   * No authentication required
   */
  async getPublicRewardProfile(token: string): Promise<PublicRewardProfile> {
    const response = await api.get<PublicRewardProfile>(
      `/rewards/public/${token}`
    );
    return response.data;
  },
};
